import { CreateStepOptions, StepTo } from "@/utils/stepper";

type BaseStep<Context, Id extends string> = Omit<
  CreateStepOptions<Context, Id>,
  "to"
>;

type CreateStepFromBase<
  TBaseStep extends { id: string },
  CustomId extends string,
> = Omit<TBaseStep, "id"> & { id: CustomId; to: StepTo };

export function createStepCreator<Context>() {
  function createBaseSteps<
    Id extends string,
    TStep extends BaseStep<Context, Id>,
  >(baseSteps: TStep[]) {
    function createSteps<
      CustomId extends string,
      Options extends {
        [Id in TStep["id"]]: {
          id: CustomId;
          to: StepTo;
        };
      },
    >(options: Options) {
      function createStepFromBase<
        TBaseStep extends { id: string },
        CustomId extends string,
      >(
        baseStep: TBaseStep,
        { id, to }: { id: CustomId; to: StepTo },
      ): CreateStepFromBase<TBaseStep, CustomId> {
        return { ...baseStep, id, to };
      }

      return baseSteps.map(<T extends TStep>(baseStep: T) => {
        return createStepFromBase(baseStep, options[baseStep.id]) as T extends T
          ? ReturnType<typeof createStepFromBase<T, Options[T["id"]]["id"]>>
          : never;
      });
    }

    return { baseSteps, createSteps };
  }

  return { createBaseSteps };
}
